import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Trash2, Loader2, Truck, Edit2 } from 'lucide-react';
import { updateClient, getClientById, createSlab, updateSlab, deleteSlab, getAllSlabs } from '../../api/createClientApi';
import { createRoute, getAllRoutes, updateRoute, deleteRoute } from '../../api/routesApi';
import { getVehiclesByClientId, getAllVehicles, assignVehicleToClient, deleteVehicleClientAssignment, addVehicle, getAllVehicleClientAssignments } from '../../api/vehicalsManagementApi';
import CustomSelect from '../shared/CustomSelect';
import { showConfirm } from '../shared/ConfirmContext';

const FormInput = ({ label, placeholder, width = '100%', value, onChange, type = 'text' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', width }}>
    <label style={{
      position: 'absolute', top: '-8px', left: '12px', backgroundColor: 'white',
      padding: '0 4px', fontSize: '11px', color: '#666', fontWeight: 500, zIndex: 1
    }}>
      {label}
    </label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      style={{
        padding: '12px 14px', border: '1px solid #D1D5DB', borderRadius: '4px',
        fontSize: '14px', color: '#333', outline: 'none', backgroundColor: 'transparent',
        width: '100%', boxSizing: 'border-box'
      }}
    />
  </div>
);

const RateInput = ({ label, placeholder, value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', width: '130px', flexShrink: 0 }}>
    <label style={{
      position: 'absolute', top: '-8px', left: '12px', backgroundColor: 'white',
      padding: '0 4px', fontSize: '11px', color: '#666', fontWeight: 500, zIndex: 1
    }}>
      {label}
    </label>
    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #D1D5DB', borderRadius: '4px', padding: '0 12px' }}>
      <input
        type="number"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{ 
          padding: '12px 0', border: 'none', fontSize: '14px', color: '#333', outline: 'none', 
          backgroundColor: 'transparent', flex: 1, minWidth: 0,
          WebkitAppearance: 'none', MozAppearance: 'textfield'
        }}
      />
      <span style={{ fontSize: '12px', color: '#666', paddingLeft: '8px' }}>Rs/-</span>
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#333', margin: '24px 0 16px' }}>
    {children}
  </h3>
);

const TabButton = ({ id, label, active, onClick }) => (
  <button
    onClick={() => onClick(id)}
    style={{
      padding: '12px 24px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: active ? 700 : 500,
      color: active ? '#20B967' : '#666',
      borderBottom: active ? '2px solid #20B967' : '2px solid transparent',
      transition: 'all 0.2s'
    }}
  >
    {label}
  </button>
);

const EditClientModal = ({ isOpen, onClose, clientId, onSave }) => {
  const [activeTab, setActiveTab] = useState('client');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };
  const [clientData, setClientData] = useState({
    clientName: '',
    contactPerson: '',
    contactNumber: '',
    address: '',
    email: ''
  });
  
  // Slabs state
  const [slabs, setSlabs] = useState([]);
  const [slabsLoading, setSlabsLoading] = useState(false);
  const [newSlabFrom, setNewSlabFrom] = useState('');
  const [newSlabTo, setNewSlabTo] = useState('');
  const [editingSlabId, setEditingSlabId] = useState(null);
  const [editSlabData, setEditSlabData] = useState({});
  const [showAddSlabForm, setShowAddSlabForm] = useState(true);
  
  // Routes state (from routesApi)
  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [editRouteData, setEditRouteData] = useState({});

  // Vehicles state
  const [allVehicles, setAllVehicles] = useState([]);
  const [vehicleAssignments, setVehicleAssignments] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState('');
  const [showAddVehicleForm, setShowAddVehicleForm] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    vehicleNumber: '',
    registrationNumber: '',
    vehicleType: '',
    driverName: '',
    clientId: ''
  });

  // Fetch client data when modal opens
  useEffect(() => {
    if (isOpen && clientId) {
      fetchClientData();
      fetchSlabs();
      fetchRoutes();
      fetchVehicles();
      fetchAllAvailableVehicles();
      fetchVehicleAssignments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, clientId]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      const data = await getClientById(clientId);
      
      // Handle different response structures
      const client = data?.client || data?.data || data;
      
      if (client) {
        setClientData({
          clientName: client.clientName || client.client_name || client.companyName || client.company_name || client.name || '',
          contactPerson: client.contactPerson || client.contact_person || client.contact || client.contact_name || '',
          contactNumber: client.contactNumber || client.contact_number || client.phone || client.mobile || client.phoneNumber || '',
          address: client.address || client.location || client.street || '',
          email: client.email || client.emailAddress || client.mail || ''
        });
      }
    } catch (err) {
      showToast('Failed to load client data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSlabs = async () => {
    try {
      setSlabsLoading(true);
      
      // Pass clientId as a filter to the API
      const data = await getAllSlabs({ clientId });
      
      // API returns {clientStores: [...]} - extract slabs from clientStores
      let allSlabs = [];
      if (data && data.clientStores && Array.isArray(data.clientStores)) {
        allSlabs = data.clientStores;
      } else if (data && Array.isArray(data)) {
        allSlabs = data;
      } else if (data && data.slabs) {
        allSlabs = data.slabs;
      } else if (data && data.data) {
        allSlabs = data.data;
      }
      
      
      // Debug: Log the first slab's keys to see actual field names from backend
      if (allSlabs.length > 0) {
      }
      
      // Map API data to component format
      const mappedSlabs = allSlabs.map((s, index) => ({
        ...s,
        tempId: s.id || Date.now() + index,
        from: s.startRange || s.start_range || s.fromKm || s.from || '',
        to: s.endRange || s.end_range || s.toKm || s.to || '',
        sedanRate: s.sedanRate || s.sedan_rate || '',
        suvRate: s.suvRate || s.suv_rate || '',
        escortSedanRate: s.escortForSedanRate || s.escort_for_sedan_rate || s.escortSedanRate || s.escort_sedan_rate || s.escortRate || s.escort_rate || '',
        escortSuvRate: s.escortForSuvRate || s.escort_for_suv_rate || s.escortSuvRate || s.escort_suv_rate || s.escortRate || s.escort_rate || '',
        slabType: s.slabType || s.slab_type || 'Slab'
      }));
      
      setSlabs(mappedSlabs);
    } catch (err) {
    } finally {
      setSlabsLoading(false);
    }
  };

  const fetchRoutes = async () => {
    try {
      setRoutesLoading(true);
      const data = await getAllRoutes();
      const allRoutes = data.routes || data.data || [];
      // Filter routes for this client
      const clientRoutes = allRoutes.filter(r =>
        r.clientId === clientId || r.client_id === clientId
      );
      setRoutes(clientRoutes.map((r, index) => ({
        ...r,
        tempId: r.id || Date.now() + index,
        distance: r.distanceKm || r.distance_km || r.distance || 0
      })));
    } catch (err) {
    } finally {
      setRoutesLoading(false);
    }
  };

  // Vehicles operations
  const fetchVehicles = async () => {
    try {
      setVehiclesLoading(true);
      await getVehiclesByClientId(clientId);
    } catch (err) {
    } finally {
      setVehiclesLoading(false);
    }
  };

  const fetchAllAvailableVehicles = async () => {
    try {
      const data = await getAllVehicles();
      const vehiclesList = data.vehicles || data.data || [];
      setAllVehicles(vehiclesList);
    } catch (err) {
    }
  };

  const fetchVehicleAssignments = async () => {
    try {
      const data = await getAllVehicleClientAssignments();
      const assignments = data.assignments || data.data || [];
      setVehicleAssignments(assignments);
    } catch (err) {
    }
  };

  const handleCreateNewVehicle = async () => {
    if (!newVehicle.vehicleNumber.trim()) {
      showToast('Please enter vehicle number', 'error');
      return;
    }
    try {
      setSaving(true);
      const result = await addVehicle({
        vehicleNumber: newVehicle.vehicleNumber,
        registrationNumber: newVehicle.registrationNumber,
        vehicleType: newVehicle.vehicleType,
        driverName: newVehicle.driverName,
        status: 'Active'
      });
      
      if (result.success) {
        // If client selected, assign vehicle to client
        if (newVehicle.clientId) {
          const vehicleId = result.vehicle?.id || result.data?.id;
          if (vehicleId) {
            await assignVehicleToClient(newVehicle.clientId, vehicleId);
          }
        }
        showToast('Vehicle created successfully!');
        setNewVehicle({ vehicleNumber: '', registrationNumber: '', vehicleType: '', driverName: '', clientId: '' });
        setShowAddVehicleForm(false);
        fetchAllAvailableVehicles();
        fetchVehicleAssignments();
      } else {
        showToast(result.message || 'Failed to create vehicle', 'error');
      }
    } catch (err) {
      showToast('Error creating vehicle', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignVehicleToClient = async (vehicleId) => {
    try {
      setSaving(true);
      await assignVehicleToClient(clientId, vehicleId);
      showToast('Vehicle assigned to current client!');
      fetchVehicleAssignments();
      fetchVehicles();
    } catch (err) {
      showToast('Failed to assign vehicle', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveVehicleFromClient = async (vehicleId, assignmentId) => {
    if (!assignmentId) {
      showToast('Vehicle is not assigned to any client', 'error');
      return;
    }
    const confirmed = await showConfirm({
      title: 'Remove Vehicle Assignment',
      message: 'Are you sure you want to remove this vehicle assignment?',
      danger: true,
      confirmLabel: 'Remove',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;
    try {
      setSaving(true);
      await deleteVehicleClientAssignment(assignmentId);
      showToast('Vehicle assignment removed!');
      fetchVehicleAssignments();
      fetchVehicles();
    } catch (err) {
      showToast('Failed to remove assignment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateVehicleAssignment = async (vehicleId, assignmentId) => {
    // For now, just toggle assignment - remove and re-add to current client
    try {
      setSaving(true);
      if (assignmentId) {
        await deleteVehicleClientAssignment(assignmentId);
      }
      await assignVehicleToClient(clientId, vehicleId);
      showToast('Vehicle assignment updated!');
      fetchVehicleAssignments();
      fetchVehicles();
    } catch (err) {
      showToast('Failed to update assignment', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Filter vehicles based on search term
  const filteredAllVehicles = useMemo(() => {
    if (!vehicleSearchTerm) return allVehicles;
    return allVehicles.filter(v => {
      const vehicleNum = (v.vehicleNumber || v.vehicle_number || v.registrationNumber || '').toLowerCase();
      return vehicleNum.includes(vehicleSearchTerm.toLowerCase());
    });
  }, [allVehicles, vehicleSearchTerm]);

  const handleUpdateField = async (fieldName, value) => {
    try {
      setSaving(true);
      const payload = { [fieldName]: value };
      const result = await updateClient(clientId, payload);
      if (result.success !== false && result.message !== "Failed to update client") {
        showToast(`${fieldName} updated successfully!`, 'success');
        onSave?.();
      } else {
        showToast(result.message || `Failed to update ${fieldName}`, 'error');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || `Error updating ${fieldName}`;
      showToast(errorMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClient = async () => {
    if (!clientData.clientName.trim()) {
      showToast('Please enter client name', 'error');
      return;
    }
    try {
      setSaving(true);
      const result = await updateClient(clientId, clientData);
      if (result.success !== false && result.message !== "Failed to update client") {
        showToast('Client updated successfully!', 'success');
        setTimeout(() => {
          onClose();
          onSave?.();
        }, 800);
      } else {
        showToast(result.message || 'Failed to update client', 'error');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error updating client';
      showToast(errorMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Slab operations
  const addSlab = () => {
    if (!newSlabFrom.trim() || !newSlabTo.trim()) {
      showToast('Please enter both From and To values', 'error');
      return;
    }
    setSlabs(prev => [...prev, { 
      tempId: Date.now(), 
      from: newSlabFrom, 
      to: newSlabTo, 
      sedanRate: '',
      suvRate: '',
      escortSedanRate: '',
      escortSuvRate: '',
      slabType: 'Slab',
      isNew: true 
    }]);
    setNewSlabFrom('');
    setNewSlabTo('');
  };

  const removeSlab = async (tempId) => {
    const slab = slabs.find(s => s.tempId === tempId);
    if (slab && !slab.isNew && slab.id) {
      const confirmed = await showConfirm({
        title: 'Delete Slab',
        message: 'Are you sure you want to delete this slab?',
        danger: true,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
      });
      if (!confirmed) return;
      try {
        await deleteSlab(slab.id);
      } catch (err) {
        showToast('Failed to delete slab', 'error');
        return;
      }
    }
    setSlabs(prev => prev.filter(s => s.tempId !== tempId));
  };

  const updateSlabField = (tempId, field, value) => {
    setSlabs(prev => prev.map(s => 
      s.tempId === tempId ? { ...s, [field]: value } : s
    ));
  };

  const startEditingSlab = (slab) => {
    setEditingSlabId(slab.tempId);
    setEditSlabData({
      from: slab.from || '',
      to: slab.to || '',
      sedanRate: slab.sedanRate || '',
      suvRate: slab.suvRate || '',
      escortSedanRate: slab.escortSedanRate || '',
      escortSuvRate: slab.escortSuvRate || '',
      slabType: slab.slabType || 'Slab'
    });
  };

  const cancelEditingSlab = () => {
    setEditingSlabId(null);
    setEditSlabData({});
  };

  const updateEditSlabField = (field, value) => {
    setEditSlabData(prev => ({ ...prev, [field]: value }));
  };

  const saveEditedSlab = async (tempId) => {
    const slab = slabs.find(s => s.tempId === tempId);
    if (!slab || !slab.id) return;
    
    try {
      setSaving(true);
      const payload = {
        clientId: clientId,
        slabType: editSlabData.slabType || 'Slab',
        startRange: String(editSlabData.from || 0),
        endRange: String(editSlabData.to || 0),
        vehicleType: 'Sedan',
        sedanRate: String(editSlabData.sedanRate || ''),
        suvRate: String(editSlabData.suvRate || ''),
        escortSedanRate: String(editSlabData.escortSedanRate || ''),
        escortSuvRate: String(editSlabData.escortSuvRate || '')
      };
      await updateSlab(slab.id, payload);
      showToast('Slab updated successfully!');
      setEditingSlabId(null);
      setEditSlabData({});
      fetchSlabs();
    } catch (err) {
      showToast('Error updating slab', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveSlabs = async () => {
    try {
      setSaving(true);
      for (const slab of slabs) {
        const payload = {
          clientId: clientId,
          slabType: slab.slabType || 'Slab',
          startRange: String(slab.from || 0),
          endRange: String(slab.to || 0),
          vehicleType: 'Sedan',
          sedanRate: String(slab.sedanRate || ''),
          suvRate: String(slab.suvRate || ''),
          escortSedanRate: String(slab.escortSedanRate || ''),
          escortSuvRate: String(slab.escortSuvRate || '')
        };
        
        if (slab.isNew) {
          await createSlab(payload);
        } else if (slab.id) {
          await updateSlab(slab.id, payload);
        }
      }
      showToast('Slabs saved successfully!');
      setTimeout(() => {
        onClose();
        onSave?.();
      }, 600);
    } catch (err) {
      showToast('Error saving slabs', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Route operations
  const addRoute = () => {
    setRoutes(prev => [...prev, {
      tempId: Date.now(),
      routeName: '',
      fromLocation: '',
      toLocation: '',
      distance: '',
      isNew: true
    }]);
  };

  const removeRoute = async (tempId) => {
    const route = routes.find(r => r.tempId === tempId);
    if (route && !route.isNew && route.id) {
      const confirmed = await showConfirm({
        title: 'Delete Route',
        message: 'Are you sure you want to delete this route?',
        danger: true,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
      });
      if (!confirmed) return;
      try {
        await deleteRoute(route.id);
      } catch (err) {
        showToast('Failed to delete route', 'error');
        return;
      }
    }
    setRoutes(prev => prev.filter(r => r.tempId !== tempId));
  };

  const updateRouteField = (tempId, field, value) => {
    setRoutes(prev => prev.map(r =>
      r.tempId === tempId ? { ...r, [field]: value } : r
    ));
  };

  const startEditingRoute = (route) => {
    setEditingRouteId(route.tempId);
    setEditRouteData({
      routeName: route.routeName || '',
      fromLocation: route.fromLocation || '',
      toLocation: route.toLocation || '',
      distance: route.distance || ''
    });
  };

  const cancelEditingRoute = () => {
    setEditingRouteId(null);
    setEditRouteData({});
  };

  const updateEditRouteField = (field, value) => {
    setEditRouteData(prev => ({ ...prev, [field]: value }));
  };

  const saveEditedRoute = async (tempId) => {
    const route = routes.find(r => r.tempId === tempId);
    if (!route || !route.id) return;
    
    try {
      setSaving(true);
      const payload = {
        routeName: editRouteData.routeName || '',
        fromLocation: editRouteData.fromLocation || '',
        toLocation: editRouteData.toLocation || '',
        distanceKm: parseFloat(editRouteData.distance) || 0
      };
      await updateRoute(route.id, payload);
      showToast('Route updated successfully!');
      setEditingRouteId(null);
      setEditRouteData({});
      fetchRoutes();
    } catch (err) {
      showToast('Error updating route', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveRoutes = async () => {
    try {
      setSaving(true);
      for (const route of routes) {
        const payload = {
          client_id: clientId,
          routeName: route.routeName || '',
          fromLocation: route.fromLocation || '',
          toLocation: route.toLocation || '',
          distanceKm: parseFloat(route.distance) || 0
        };

        if (route.isNew) {
          await createRoute(payload);
        } else if (route.id) {
          await updateRoute(route.id, payload);
        }
      }
      showToast('Routes saved successfully!');
      setTimeout(() => {
        onClose();
        onSave?.();
      }, 600);
    } catch (err) {
      showToast('Error saving routes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveVehicles = async () => {
    try {
      setSaving(true);
      showToast('Vehicle assignments saved successfully!');
      setTimeout(() => {
        onClose();
        onSave?.();
      }, 600);
    } catch (err) {
      showToast('Error saving vehicle assignments', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          backgroundColor: toast.type === 'error' ? '#FEF2F2' : '#E8F5EE',
          color: toast.type === 'error' ? '#D92D20' : '#07A95B',
          border: `1px solid ${toast.type === 'error' ? '#FECDCA' : '#A6E5C5'}`,
          borderRadius: '8px',
          padding: '12px 20px',
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>{toast.message}</span>
        </div>
      )}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        backgroundColor: 'white', zIndex: 2001, width: '90%', maxWidth: '1000px',
        boxShadow: '0 24px 48px rgba(0,0,0,0.2)', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        borderRadius: '8px', overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ backgroundColor: '#227B48', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'white' }}>Edit Client</h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#F04438',
            display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 500
          }}>
            Close <X size={18} style={{ backgroundColor: '#F04438', color: 'white', borderRadius: '50%', padding: '2px' }} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: '1px solid #E5E7EB', display: 'flex', padding: '0 32px', flexShrink: 0 }}>
          <TabButton id="client" label="Client Info" active={activeTab === 'client'} onClick={setActiveTab} />
          <TabButton id="slabs" label="Slabs" active={activeTab === 'slabs'} onClick={setActiveTab} />
          <TabButton id="routes" label="Routes" active={activeTab === 'routes'} onClick={setActiveTab} />
          <TabButton id="vehicles" label="Vehicles" active={activeTab === 'vehicles'} onClick={setActiveTab} />
        </div>

        {/* Content */}
        <div style={{ padding: '32px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', color: '#666' }}>
              <Loader2 size={24} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} /> Loading...
            </div>
          ) : (
            <>
              {/* Client Info Tab */}
              {activeTab === 'client' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <SectionTitle>Client Details</SectionTitle>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {/* Client Name - read only, no update button */}
                    <FormInput 
                      label="Client Name" 
                      placeholder="Enter client name" 
                      value={clientData.clientName}
                      onChange={() => {}}
                      readOnly
                    />
                    {/* Contact Person with update button */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <FormInput 
                          label="Contact Person" 
                          placeholder="Contact Person Name"
                          value={clientData.contactPerson}
                          onChange={(e) => setClientData({...clientData, contactPerson: e.target.value})}
                        />
                      </div>
                      <button
                        onClick={() => handleUpdateField('contactPerson', clientData.contactPerson)}
                        disabled={saving}
                        style={{
                          marginTop: '8px',
                          backgroundColor: '#3B82F6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: saving ? 0.6 : 1
                        }}
                        title="Update Contact Person"
                      >
                        <Edit2 size={16} />
                      </button>
                    </div>
                    {/* Contact Number with update button */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <FormInput 
                          label="Contact Number" 
                          placeholder="Contact Number"
                          value={clientData.contactNumber}
                          onChange={(e) => setClientData({...clientData, contactNumber: e.target.value})}
                        />
                      </div>
                      <button
                        onClick={() => handleUpdateField('contactNumber', clientData.contactNumber)}
                        disabled={saving}
                        style={{
                          marginTop: '8px',
                          backgroundColor: '#3B82F6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: saving ? 0.6 : 1
                        }}
                        title="Update Contact Number"
                      >
                        <Edit2 size={16} />
                      </button>
                    </div>
                    {/* Email with update button */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <FormInput 
                          label="Email" 
                          placeholder="Email Address"
                          type="email"
                          value={clientData.email}
                          onChange={(e) => setClientData({...clientData, email: e.target.value})}
                        />
                      </div>
                      <button
                        onClick={() => handleUpdateField('email', clientData.email)}
                        disabled={saving}
                        style={{
                          marginTop: '8px',
                          backgroundColor: '#3B82F6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: saving ? 0.6 : 1
                        }}
                        title="Update Email"
                      >
                        <Edit2 size={16} />
                      </button>
                    </div>
                    {/* Address with update button - full width */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', gridColumn: '1 / -1' }}>
                      <div style={{ flex: 1 }}>
                        <FormInput 
                          label="Address" 
                          placeholder="Enter Address"
                          width="100%"
                          value={clientData.address}
                          onChange={(e) => setClientData({...clientData, address: e.target.value})}
                        />
                      </div>
                      <button
                        onClick={() => handleUpdateField('address', clientData.address)}
                        disabled={saving}
                        style={{
                          marginTop: '8px',
                          backgroundColor: '#3B82F6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: saving ? 0.6 : 1
                        }}
                        title="Update Address"
                      >
                        <Edit2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Slabs Tab */}
              {activeTab === 'slabs' && (
                <div>
                  {/* Loading state */}
                  {slabsLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', color: '#666' }}>
                      <Loader2 size={24} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} /> Loading slabs...
                    </div>
                  )}

                  {/* Existing Slabs Section - List View */}
                  {!slabsLoading && slabs.filter(s => !s.isNew).length > 0 && (
                    <>
                      <SectionTitle>Existing Slabs ({slabs.filter(s => !s.isNew).length})</SectionTitle>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
                        {slabs.filter(s => !s.isNew).map((slab) => (
                          <div key={slab.tempId}>
                            {editingSlabId === slab.tempId ? (
                              /* Edit Mode - Show Input Fields */
                              <div style={{ 
                                display: 'flex', 
                                gap: '12px', 
                                alignItems: 'center',
                                padding: '16px',
                                backgroundColor: '#FEF3C7',
                                borderRadius: '8px',
                                border: '1px solid #F59E0B'
                              }}>
                                <FormInput 
                                  label="From" 
                                  placeholder="Start"
                                  width="100px"
                                  value={editSlabData.from}
                                  onChange={(e) => updateEditSlabField('from', e.target.value)}
                                />
                                <FormInput 
                                  label="To" 
                                  placeholder="End"
                                  width="100px"
                                  value={editSlabData.to}
                                  onChange={(e) => updateEditSlabField('to', e.target.value)}
                                />
                                <RateInput 
                                  label="Sedan" 
                                  placeholder="500"
                                  value={editSlabData.sedanRate}
                                  onChange={(e) => updateEditSlabField('sedanRate', e.target.value)}
                                />
                                <RateInput 
                                  label="SUV" 
                                  placeholder="700"
                                  value={editSlabData.suvRate}
                                  onChange={(e) => updateEditSlabField('suvRate', e.target.value)}
                                />
                                <RateInput 
                                  label="Sedan Escort" 
                                  placeholder="600"
                                  value={editSlabData.escortSedanRate}
                                  onChange={(e) => updateEditSlabField('escortSedanRate', e.target.value)}
                                />
                                <RateInput 
                                  label="SUV Escort" 
                                  placeholder="700"
                                  value={editSlabData.escortSuvRate}
                                  onChange={(e) => updateEditSlabField('escortSuvRate', e.target.value)}
                                />
                                
                                {/* Save Button */}
                                <button
                                  onClick={() => saveEditedSlab(slab.tempId)}
                                  disabled={saving}
                                  style={{
                                    backgroundColor: '#20B967', color: 'white', border: 'none', borderRadius: '6px',
                                    padding: '12px', cursor: saving ? 'not-allowed' : 'pointer', height: '48px', minWidth: '80px', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: saving ? 0.6 : 1, fontSize: '13px', fontWeight: 600
                                  }}
                                >
                                  {saving ? '...' : 'Save'}
                                </button>
                                
                                {/* Cancel Button */}
                                <button
                                  onClick={cancelEditingSlab}
                                  style={{
                                    backgroundColor: '#6B7280', color: 'white', border: 'none', borderRadius: '6px',
                                    padding: '12px', cursor: 'pointer', height: '48px', minWidth: '80px', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '13px', fontWeight: 600
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              /* List View Mode - Show Read Only with Update/Delete Buttons */
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                padding: '16px 20px',
                                backgroundColor: '#F0FDF4',
                                borderRadius: '8px',
                                border: '1px solid #BBF7D0',
                                justifyContent: 'space-between'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '40px', flex: 1 }}>
                                  <div style={{ minWidth: '120px' }}>
                                    <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Range</span>
                                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>
                                      {slab.from || '0'} km - {slab.to || '0'} km
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', gap: '32px' }}>
                                    <div>
                                      <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '2px' }}>Sedan</span>
                                      <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>₹{slab.sedanRate || '-'}</span>
                                    </div>
                                    <div>
                                      <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '2px' }}>SUV</span>
                                      <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>₹{slab.suvRate || '-'}</span>
                                    </div>
                                    <div>
                                      <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '2px' }}>Sedan Escort</span>
                                      <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>₹{slab.escortSedanRate || '-'}</span>
                                    </div>
                                    <div>
                                      <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '2px' }}>SUV Escort</span>
                                      <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>₹{slab.escortSuvRate || '-'}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  {/* Update Button */}
                                  <button
                                    onClick={() => startEditingSlab(slab)}
                                    style={{
                                      backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '6px',
                                      padding: '10px 16px', cursor: 'pointer', height: '40px', display: 'flex',
                                      alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600
                                    }}
                                  >
                                    <Edit2 size={16} /> Update
                                  </button>
                                  
                                  {/* Delete Button */}
                                  <button
                                    onClick={() => removeSlab(slab.tempId)}
                                    style={{
                                      backgroundColor: '#F04438', color: 'white', border: 'none', borderRadius: '6px',
                                      padding: '10px', cursor: 'pointer', height: '40px', width: '40px', display: 'flex',
                                      alignItems: 'center', justifyContent: 'center'
                                    }}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Empty state - no existing slabs */}
                  {!slabsLoading && slabs.filter(s => !s.isNew).length === 0 && (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#888', backgroundColor: '#F9FAFB', borderRadius: '8px', marginBottom: '24px' }}>
                      <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>No existing slabs found for this client.</p>
                      <p style={{ margin: '0', fontSize: '14px' }}>Click "Add New Slab" below to create one.</p>
                    </div>
                  )}

                  {/* Slab Type Selection */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', padding: '12px 16px', backgroundColor: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#166534' }}>Slab Type:</span>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setSlabs(prev => prev.filter(s => s.slabType !== 'Fixed'));
                          setShowAddSlabForm(true);
                        }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: slabs.some(s => s.slabType === 'Fixed') ? '#E5E7EB' : '#20B967',
                          color: slabs.some(s => s.slabType === 'Fixed') ? '#6B7280' : 'white'
                        }}
                      >
                        Slab
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const newSlab = {
                            tempId: Date.now(),
                            from: '',
                            to: '',
                            sedanRate: '',
                            suvRate: '',
                            escortSedanRate: '',
                            escortSuvRate: '',
                            slabType: 'Fixed',
                            isNew: true
                          };
                          setSlabs(prev => [...prev.filter(s => s.slabType !== 'Fixed'), newSlab]);
                          setShowAddSlabForm(false);
                        }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: slabs.some(s => s.slabType === 'Fixed') ? '#20B967' : '#E5E7EB',
                          color: slabs.some(s => s.slabType === 'Fixed') ? 'white' : '#6B7280'
                        }}
                      >
                        Fixed
                      </button>
                    </div>
                  </div>


                  {/* Slab Input Form - shown when button clicked */}
                  {showAddSlabForm && (
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '32px', padding: '20px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                      {slabs.length > 0 && slabs[0].slabType === 'Fixed' ? (
                        <div style={{ fontSize: '14px', color: '#166534', fontWeight: 600, padding: '12px 0' }}>
                          Fixed Price Rate Card
                        </div>
                      ) : (
                        <>
                          <FormInput 
                            label="From" 
                            placeholder="Start Range"
                            width="180px"
                            value={newSlabFrom}
                            onChange={(e) => setNewSlabFrom(e.target.value)}
                          />
                          <FormInput 
                            label="To" 
                            placeholder="End Range"
                            width="180px"
                            value={newSlabTo}
                            onChange={(e) => setNewSlabTo(e.target.value)}
                          />
                        </>
                      )}
                      <button
                        onClick={addSlab}
                        style={{
                          backgroundColor: '#20B967', color: 'white', border: 'none', borderRadius: '6px',
                          padding: '14px 24px', fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center',
                          gap: '8px', cursor: 'pointer', height: '48px', whiteSpace: 'nowrap'
                        }}
                      >
                        Add <Plus size={18} />
                      </button>
                      <button
                        onClick={() => {
                          setShowAddSlabForm(false);
                          setNewSlabFrom('');
                          setNewSlabTo('');
                        }}
                        style={{
                          backgroundColor: '#6B7280', color: 'white', border: 'none', borderRadius: '6px',
                          padding: '14px 20px', fontSize: '15px', fontWeight: 600,
                          cursor: 'pointer', height: '48px', whiteSpace: 'nowrap'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  {/* Rate Card Section - Only for new slabs */}
                  <SectionTitle>Rate Card for New Slabs</SectionTitle>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {slabs.filter(s => s.isNew).length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#888', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                        No new slabs added. Click "Add New Slab" to create one.
                      </div>
                    ) : (
                      slabs.filter(s => s.isNew).map((slab) => (
                        <div key={slab.tempId} style={{ 
                          display: 'flex', 
                          gap: '16px', 
                          alignItems: 'center'
                        }}>
                          {/* Slab Range Display */}
                          <div style={{ 
                            backgroundColor: slab.slabType === 'Fixed' ? '#F0FDF4' : '#F3F4F6', 
                            padding: '14px 20px', 
                            borderRadius: '4px', 
                            fontSize: '14px', 
                            color: slab.slabType === 'Fixed' ? '#166534' : '#6B7280', 
                            fontWeight: 600, 
                            minWidth: '120px',
                            textAlign: 'center'
                          }}>
                            {slab.slabType === 'Fixed' ? 'Fixed' : `${slab.from || '0'}km to ${slab.to || '0'}km`}
                          </div>
                          
                          {/* Rate Inputs */}
                          <RateInput 
                            label="Sedan" 
                            placeholder="₹"
                            value={slab.sedanRate || ''}
                            onChange={(e) => updateSlabField(slab.tempId, 'sedanRate', e.target.value)}
                          />
                          <RateInput 
                            label="SUV" 
                            placeholder="₹"
                            value={slab.suvRate || ''}
                            onChange={(e) => updateSlabField(slab.tempId, 'suvRate', e.target.value)}
                          />
                          <RateInput 
                            label="Sedan Escort" 
                            placeholder="₹"
                            value={slab.escortSedanRate || ''}
                            onChange={(e) => updateSlabField(slab.tempId, 'escortSedanRate', e.target.value)}
                          />
                          <RateInput 
                            label="SUV Escort" 
                            placeholder="₹"
                            value={slab.escortSuvRate || ''}
                            onChange={(e) => updateSlabField(slab.tempId, 'escortSuvRate', e.target.value)}
                          />
                          
                          {/* Delete Button */}
                          <button
                            onClick={() => removeSlab(slab.tempId)}
                            style={{
                              backgroundColor: '#F04438', color: 'white', border: 'none', borderRadius: '6px',
                              padding: '12px', cursor: 'pointer', height: '48px', width: '48px', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Routes Tab */}
              {activeTab === 'routes' && (
                <div>
                  <SectionTitle>Manage Routes</SectionTitle>

                  {routesLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', color: '#666' }}>
                      <Loader2 size={24} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} /> Loading routes...
                    </div>
                  )}

                  {/* Existing Routes Section - List View */}
                  {!routesLoading && routes.filter(r => !r.isNew).length > 0 && (
                    <>
                      <SectionTitle>Existing Routes ({routes.filter(r => !r.isNew).length})</SectionTitle>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
                        {routes.filter(r => !r.isNew).map((route) => (
                          <div key={route.tempId}>
                            {editingRouteId === route.tempId ? (
                              /* Edit Mode - Show Input Fields */
                              <div style={{ 
                                display: 'flex', 
                                gap: '12px', 
                                alignItems: 'center',
                                padding: '16px',
                                backgroundColor: '#FEF3C7',
                                borderRadius: '8px',
                                border: '1px solid #F59E0B'
                              }}>
                                <FormInput 
                                  label="Route Name" 
                                  placeholder="e.g., Airport Transfer"
                                  width="200px"
                                  value={editRouteData.routeName}
                                  onChange={(e) => updateEditRouteField('routeName', e.target.value)}
                                />
                                <FormInput 
                                  label="From Location" 
                                  placeholder="Start location"
                                  width="160px"
                                  value={editRouteData.fromLocation}
                                  onChange={(e) => updateEditRouteField('fromLocation', e.target.value)}
                                />
                                <FormInput 
                                  label="To Location" 
                                  placeholder="End location"
                                  width="160px"
                                  value={editRouteData.toLocation}
                                  onChange={(e) => updateEditRouteField('toLocation', e.target.value)}
                                />
                                <FormInput 
                                  label="Distance (KM)" 
                                  placeholder="0"
                                  width="120px"
                                  value={editRouteData.distance}
                                  onChange={(e) => updateEditRouteField('distance', e.target.value)}
                                />
                                
                                {/* Save Button */}
                                <button
                                  onClick={() => saveEditedRoute(route.tempId)}
                                  disabled={saving}
                                  style={{
                                    backgroundColor: '#20B967', color: 'white', border: 'none', borderRadius: '6px',
                                    padding: '12px', cursor: saving ? 'not-allowed' : 'pointer', height: '48px', minWidth: '80px', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: saving ? 0.6 : 1, fontSize: '13px', fontWeight: 600
                                  }}
                                >
                                  {saving ? '...' : 'Save'}
                                </button>
                                
                                {/* Cancel Button */}
                                <button
                                  onClick={cancelEditingRoute}
                                  style={{
                                    backgroundColor: '#6B7280', color: 'white', border: 'none', borderRadius: '6px',
                                    padding: '12px', cursor: 'pointer', height: '48px', minWidth: '80px', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '13px', fontWeight: 600
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              /* List View Mode - Show Read Only with Update/Delete Buttons */
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                padding: '16px 20px',
                                backgroundColor: '#F0FDF4',
                                borderRadius: '8px',
                                border: '1px solid #BBF7D0',
                                justifyContent: 'space-between'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '40px', flex: 1 }}>
                                  <div style={{ minWidth: '150px' }}>
                                    <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Route Name</span>
                                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>
                                      {route.routeName || '-'}
                                    </span>
                                  </div>
                                  <div style={{ minWidth: '140px' }}>
                                    <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>From</span>
                                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>{route.fromLocation || '-'}</span>
                                  </div>
                                  <div style={{ minWidth: '140px' }}>
                                    <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>To</span>
                                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>{route.toLocation || '-'}</span>
                                  </div>
                                  <div style={{ minWidth: '100px' }}>
                                    <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Distance</span>
                                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>{route.distance || '0'} km</span>
                                  </div>
                                </div>
                                
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  {/* Update Button */}
                                  <button
                                    onClick={() => startEditingRoute(route)}
                                    style={{
                                      backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '6px',
                                      padding: '10px 16px', cursor: 'pointer', height: '40px', display: 'flex',
                                      alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600
                                    }}
                                  >
                                    <Edit2 size={16} /> Update
                                  </button>
                                  
                                  {/* Delete Button */}
                                  <button
                                    onClick={() => removeRoute(route.tempId)}
                                    style={{
                                      backgroundColor: '#F04438', color: 'white', border: 'none', borderRadius: '6px',
                                      padding: '10px', cursor: 'pointer', height: '40px', width: '40px', display: 'flex',
                                      alignItems: 'center', justifyContent: 'center'
                                    }}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Empty state - no existing routes */}
                  {!routesLoading && routes.filter(r => !r.isNew).length === 0 && (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#888', backgroundColor: '#F9FAFB', borderRadius: '8px', marginBottom: '24px' }}>
                      <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>No existing routes found for this client.</p>
                      <p style={{ margin: '0', fontSize: '14px' }}>Click "Add New Route" below to create one.</p>
                    </div>
                  )}

                  {/* Add New Route Section */}
                  <SectionTitle>Add New Route</SectionTitle>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {routes.filter(r => r.isNew).length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#888', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                        No new routes to add. Click "Add New Route" button to create one.
                      </div>
                    ) : (
                      routes.filter(r => r.isNew).map((route) => (
                        <div key={route.tempId} style={{ 
                          display: 'flex', 
                          gap: '16px', 
                          alignItems: 'center'
                        }}>
                          <FormInput 
                            label="Route Name" 
                            placeholder="e.g., Airport Transfer"
                            width="200px"
                            value={route.routeName || ''}
                            onChange={(e) => updateRouteField(route.tempId, 'routeName', e.target.value)}
                          />
                          <FormInput 
                            label="From Location" 
                            placeholder="Start location"
                            width="160px"
                            value={route.fromLocation || ''}
                            onChange={(e) => updateRouteField(route.tempId, 'fromLocation', e.target.value)}
                          />
                          <FormInput 
                            label="To Location" 
                            placeholder="End location"
                            width="160px"
                            value={route.toLocation || ''}
                            onChange={(e) => updateRouteField(route.tempId, 'toLocation', e.target.value)}
                          />
                          <FormInput 
                            label="Distance (KM)" 
                            placeholder="0"
                            width="120px"
                            value={route.distance || ''}
                            onChange={(e) => updateRouteField(route.tempId, 'distance', e.target.value)}
                          />
                          
                          {/* Delete Button */}
                          <button
                            onClick={() => removeRoute(route.tempId)}
                            style={{
                              backgroundColor: '#F04438', color: 'white', border: 'none', borderRadius: '6px',
                              padding: '12px', cursor: 'pointer', height: '48px', width: '48px', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  <div style={{ marginTop: '24px' }}>
                    <button
                      onClick={addRoute}
                      style={{
                        backgroundColor: '#20B967', color: 'white', border: 'none', borderRadius: '6px',
                        padding: '14px 24px', fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center',
                        gap: '8px', cursor: 'pointer', height: '48px', whiteSpace: 'nowrap'
                      }}
                    >
                      <Plus size={18} /> Add New Route
                    </button>
                  </div>
                </div>
              )}

              {/* Vehicles Tab */}
              {activeTab === 'vehicles' && (
                <div>
                  <SectionTitle>All Vehicles</SectionTitle>
                  
                  {/* Search and Add Section */}
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', flex: 1 }}>
                      <label style={{
                        position: 'absolute', top: '-8px', left: '12px', backgroundColor: 'white',
                        padding: '0 4px', fontSize: '11px', color: '#666', fontWeight: 500, zIndex: 1
                      }}>
                        Search by Vehicle Number
                      </label>
                      <input
                        type="text"
                        placeholder="Enter vehicle number..."
                        value={vehicleSearchTerm}
                        onChange={(e) => setVehicleSearchTerm(e.target.value)}
                        style={{
                          padding: '12px 14px', border: '1px solid #D1D5DB', borderRadius: '4px',
                          fontSize: '14px', color: '#333', outline: 'none', backgroundColor: 'white',
                          width: '100%', boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  {/* Add New Vehicle Form */}
                  {showAddVehicleForm && (
                    <div style={{ padding: '20px', backgroundColor: '#F9FAFB', borderRadius: '8px', marginBottom: '24px' }}>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>Add New Vehicle</h4>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <FormInput 
                          label="Vehicle Number" 
                          placeholder="e.g., MH12AB1234"
                          width="180px"
                          value={newVehicle.vehicleNumber}
                          onChange={(e) => setNewVehicle({...newVehicle, vehicleNumber: e.target.value})}
                        />
                        <FormInput 
                          label="Registration Number" 
                          placeholder="e.g., REG123456"
                          width="180px"
                          value={newVehicle.registrationNumber}
                          onChange={(e) => setNewVehicle({...newVehicle, registrationNumber: e.target.value})}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', width: '160px' }}>
                          <label style={{
                            position: 'absolute', top: '-8px', left: '12px', backgroundColor: '#F9FAFB',
                            padding: '0 6px', fontSize: '11px', color: '#6B7280', fontWeight: 500, zIndex: 10,
                            borderRadius: '2px', pointerEvents: 'none'
                          }}>
                            Vehicle Type
                          </label>
                          <CustomSelect
                            value={newVehicle.vehicleType}
                            onChange={(val) => setNewVehicle({...newVehicle, vehicleType: val})}
                            options={['Sedan', 'SUV', 'Hatchback', 'Minivan']}
                            placeholder="Select Type"
                            width="100%"
                          />
                        </div>
                        <FormInput 
                          label="Driver Name" 
                          placeholder="Driver name"
                          width="150px"
                          value={newVehicle.driverName}
                          onChange={(e) => setNewVehicle({...newVehicle, driverName: e.target.value})}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', width: '200px' }}>
                          <label style={{
                            position: 'absolute', top: '-8px', left: '12px', backgroundColor: '#F9FAFB',
                            padding: '0 6px', fontSize: '11px', color: '#6B7280', fontWeight: 500, zIndex: 10,
                            borderRadius: '2px', pointerEvents: 'none'
                          }}>
                            Assign to Client (Optional)
                          </label>
                          <CustomSelect
                            value={newVehicle.clientId}
                            onChange={(val) => setNewVehicle({...newVehicle, clientId: val})}
                            options={[
                              { value: '', label: '-- No Client --' },
                              { value: clientId, label: 'Current Client' },
                            ]}
                            placeholder="-- No Client --"
                            width="100%"
                          />
                        </div>
                        <button
                          onClick={handleCreateNewVehicle}
                          disabled={saving}
                          style={{
                            backgroundColor: saving ? '#9CA3AF' : '#20B967', 
                            color: 'white', border: 'none', borderRadius: '6px',
                            padding: '12px 20px', fontSize: '14px', fontWeight: 600,
                            cursor: saving ? 'not-allowed' : 'pointer', height: '44px'
                          }}
                        >
                          {saving ? 'Creating...' : 'Create Vehicle'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Vehicles List */}
                  {vehiclesLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', color: '#666' }}>
                      <Loader2 size={24} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} /> Loading vehicles...
                    </div>
                  ) : filteredAllVehicles.length === 0 ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: '#888', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                      <Truck size={48} style={{ marginBottom: '16px', color: '#D1D5DB' }} />
                      <p>No vehicles found.</p>
                      <p style={{ fontSize: '14px', marginTop: '8px' }}>Click "Add New Vehicle" to create one.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', backgroundColor: '#212A30', color: 'white', padding: '12px 16px', fontSize: '13px', fontWeight: 500, borderRadius: '6px 6px 0 0' }}>
                        <div style={{ flex: 1.2 }}>Vehicle Number</div>
                        <div style={{ flex: 0.8, textAlign: 'center' }}>Type</div>
                        <div style={{ flex: 1, textAlign: 'center' }}>Driver</div>
                        <div style={{ flex: 1.2, textAlign: 'center' }}>Assigned To</div>
                        <div style={{ flex: 0.6, textAlign: 'center' }}>Status</div>
                        <div style={{ width: '120px', textAlign: 'center' }}>Actions</div>
                      </div>
                      {filteredAllVehicles.map((vehicle, index) => {
                        const assignment = vehicleAssignments.find(a => a.vehicleId === vehicle.id || a.vehicle?.id === vehicle.id);
                        const assignedClient = assignment ? (assignment.client?.clientName || assignment.clientName || 'Unknown Client') : 'Not Assigned';
                        const isAssignedToCurrent = assignment && (assignment.clientId === clientId || assignment.client?.id === clientId);
                        
                        return (
                          <div key={vehicle.id || index} style={{ 
                            display: 'flex', 
                            padding: '14px 16px', 
                            backgroundColor: isAssignedToCurrent ? '#EEF8F3' : 'white',
                            borderBottom: '1px solid #F0F0F0',
                            alignItems: 'center',
                            fontSize: '14px'
                          }}>
                            <div style={{ flex: 1.2, fontWeight: 500, color: '#333' }}>
                              {vehicle.vehicleNumber || vehicle.vehicle_number || vehicle.registrationNumber || 'N/A'}
                            </div>
                            <div style={{ flex: 0.8, textAlign: 'center', color: '#666' }}>
                              {vehicle.vehicleType || vehicle.type || 'N/A'}
                            </div>
                            <div style={{ flex: 1, textAlign: 'center', color: '#666' }}>
                              {vehicle.driverName || vehicle.driver?.driverName || vehicle.driver?.name || 'Unassigned'}
                            </div>
                            <div style={{ flex: 1.2, textAlign: 'center' }}>
                              <span style={{ 
                                padding: '4px 10px', 
                                borderRadius: '20px', 
                                fontSize: '12px', 
                                fontWeight: 600, 
                                backgroundColor: assignment ? '#E0E7FF' : '#F3F4F6', 
                                color: assignment ? '#4338CA' : '#6B7280' 
                              }}>
                                {assignedClient}
                              </span>
                            </div>
                            <div style={{ flex: 0.6, textAlign: 'center' }}>
                              <span style={{ 
                                padding: '4px 12px', 
                                borderRadius: '20px', 
                                fontSize: '12px', 
                                fontWeight: 600, 
                                backgroundColor: vehicle.status === 'Active' ? '#EEF8F3' : '#F5F5F5', 
                                color: vehicle.status === 'Active' ? '#20B967' : '#888' 
                              }}>
                                {vehicle.status || 'Active'}
                              </span>
                            </div>
                            <div style={{ width: '120px', textAlign: 'center', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              {assignment ? (
                                <>
                                  <button
                                    onClick={() => handleUpdateVehicleAssignment(vehicle.id, assignment.id)}
                                    style={{
                                      backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '6px',
                                      padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', 
                                      justifyContent: 'center'
                                    }}
                                    title="Update Assignment"
                                  >
                                    <Edit2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleRemoveVehicleFromClient(vehicle.id, assignment?.id)}
                                    style={{
                                      backgroundColor: '#F97316', color: 'white', border: 'none', borderRadius: '6px',
                                      padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', 
                                      justifyContent: 'center', fontSize: '11px', fontWeight: 600
                                    }}
                                    title="Unassign"
                                  >
                                    Unassign
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => handleAssignVehicleToClient(vehicle.id)}
                                  style={{
                                    backgroundColor: '#20B967', color: 'white', border: 'none', borderRadius: '6px',
                                    padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', 
                                    justifyContent: 'center'
                                  }}
                                  title="Assign to Current Client"
                                >
                                  <Plus size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 32px 24px 32px', display: 'flex', justifyContent: 'flex-end', gap: '12px', flexShrink: 0, borderTop: '1px solid #E5E7EB' }}>
          <button 
            onClick={onClose}
            style={{
              backgroundColor: 'white', 
              color: '#666', 
              border: '1px solid #D1D5DB', 
              borderRadius: '6px',
              padding: '12px 24px', 
              fontSize: '14px', 
              fontWeight: 600, 
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={() => {
              if (activeTab === 'client') handleSaveClient();
              else if (activeTab === 'slabs') saveSlabs();
              else if (activeTab === 'routes') saveRoutes();
              else if (activeTab === 'vehicles') saveVehicles();
            }}
            disabled={saving}
            style={{
              backgroundColor: saving ? '#9CA3AF' : '#20B967', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px',
              padding: '12px 24px', 
              fontSize: '14px', 
              fontWeight: 600, 
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
};

export default EditClientModal;
